import { ROLES, PERMISSIONS } from '../constants';

export const hasRole = (userRoles, requiredRole) => {
    if (!userRoles || !Array.isArray(userRoles)) return false;

    return userRoles.some(role =>
        typeof role === 'string'
            ? role === requiredRole
            : role.role_name === requiredRole
    );
};

export const hasAnyRole = (userRoles, requiredRoles) => {
    if (!userRoles || !Array.isArray(userRoles) || !requiredRoles) return false;

    return requiredRoles.some(role => hasRole(userRoles, role));
};

export const hasPermission = (userPermissions, requiredPermission) => {
    if (!userPermissions || !Array.isArray(userPermissions)) return false;

    return userPermissions.some(permission =>
        typeof permission === 'string'
            ? permission === requiredPermission
            : permission.name === requiredPermission
    );
};

export const hasAnyPermission = (userPermissions, requiredPermissions) => {
    if (!userPermissions || !Array.isArray(userPermissions) || !requiredPermissions) return false;

    return requiredPermissions.some(permission => hasPermission(userPermissions, permission));
};

export const isAdmin = (userRoles) => hasRole(userRoles, ROLES.ADMIN);

export const isHR = (userRoles) => hasAnyRole(userRoles, [ROLES.ADMIN, ROLES.HR_MANAGER]);

export const isManager = (userRoles) => hasAnyRole(userRoles, [
    ROLES.ADMIN,
    ROLES.HR_MANAGER,
    ROLES.DEPARTMENT_HEAD
]);

export const canManageEmployees = (userRoles) => isHR(userRoles);

export const canApproveLeaves = (userRoles) => isManager(userRoles);

export const canViewReports = (userRoles) => isManager(userRoles);

export const canManageSettings = (userRoles) => isAdmin(userRoles);

// New menu-based permission helpers
/**
 * Check if user can view a menu
 * @param {Object} user - User object with permissions
 * @param {string} menuName - Menu name/identifier
 * @returns {boolean}
 */
export const canViewMenu = (user, menuName) => {
    if (!user || !user.permissions) return false;
    
    // Check for menu-based permission: {menuName}:view
    const viewPermission = `${menuName}:view`;
    return hasPermission(user.permissions, viewPermission);
};

/**
 * Check if user can create on a menu
 * @param {Object} user - User object with permissions
 * @param {string} menuName - Menu name/identifier
 * @returns {boolean}
 */
export const canCreateOnMenu = (user, menuName) => {
    if (!user || !user.permissions) return false;
    
    const createPermission = `${menuName}:create`;
    return hasPermission(user.permissions, createPermission);
};

/**
 * Check if user can update on a menu
 * @param {Object} user - User object with permissions
 * @param {string} menuName - Menu name/identifier
 * @returns {boolean}
 */
export const canUpdateOnMenu = (user, menuName) => {
    if (!user || !user.permissions) return false;
    
    const updatePermission = `${menuName}:update`;
    return hasPermission(user.permissions, updatePermission);
};

/**
 * Check if user can delete on a menu
 * @param {Object} user - User object with permissions
 * @param {string} menuName - Menu name/identifier
 * @returns {boolean}
 */
export const canDeleteOnMenu = (user, menuName) => {
    if (!user || !user.permissions) return false;
    
    const deletePermission = `${menuName}:delete`;
    return hasPermission(user.permissions, deletePermission);
};

/**
 * Check if user has a specific action on a menu
 * @param {Object} user - User object with permissions
 * @param {string} menuName - Menu name/identifier
 * @param {string} action - Action (view, create, update, delete, approve, etc.)
 * @returns {boolean}
 */
export const canPerformActionOnMenu = (user, menuName, action) => {
    if (!user || !user.permissions) return false;
    
    const permission = `${menuName}:${action}`;
    return hasPermission(user.permissions, permission);
};